import { Box, Typography, Stack, Alert } from "@mui/material";
import { useEcrUpdateMutation } from "api/memberPortal/ecr/ecr.api";
import {
  ECR_NOTE_RESPONSE_TYPES,
  ECR_NOTE_TYPES,
} from "api/memberPortal/ecr/ecrOptions.types";
import {
  useEcrNoteCreateMutation,
  useEcrNoteUpdateMutation,
} from "api/memberPortal/ecr/notes/ecrNotes.api";
import { useAppForm } from "components/Forms/AppForm";
import DialogModal from "components/Forms/components/modals/DialogModal";
import { useAuth } from "hooks/useAuth";
import { useState } from "react";
import { Spinner, Button } from "react-bootstrap";
import { useParams } from "react-router-dom";

import {
  EcrNoteFormProps,
  LabValidatorPrivateNoteFormSchema,
  LabValidatorPrivateNoteFormValues,
} from "./EcrNoteForm.schema";

const EcrNoteForm = ({
  show,
  handleClose,
  note,
  comment,
  replyTo,
  setAlert,
  responseType,
  noteType,
}: EcrNoteFormProps) => {
  const { currentUser, permissions } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [triggerUpdateNote] = useEcrNoteUpdateMutation();
  const [triggerAddNote] = useEcrNoteCreateMutation();
  const [triggerUpdateEcr] = useEcrUpdateMutation();

  const isNiap = permissions.role_type === "NIAP";
  const isLab = permissions.role_type === "Lab";
  const isValidator = permissions.role_type === "Validator";

  const isCancelled = responseType === ECR_NOTE_RESPONSE_TYPES.ECR_CANCELLED;

  const ecrTitle = () => {
    if (note?.gist) {
      return note?.gist;
    } else if (isCancelled) {
      return responseType;
    } else {
      return `Response to ${responseType}`;
    }
  };

  const ecrNoteForm = useAppForm({
    defaultValues: {
      gist: ecrTitle(),
      note1: note?.note1 ?? "",
      private: note?.private ?? false,
      visible_to_labs: note?.visible_to_labs ?? false,
      visible_to_validators: note?.visible_to_validators ?? false,
    } as LabValidatorPrivateNoteFormValues,
    validators: {
      onSubmit: LabValidatorPrivateNoteFormSchema,
    },
    onSubmit: async ({ value }) => {
      setError(null);
      setLoading(true);
      try {
        if (note?.upid) {
          await triggerUpdateNote({
            upid: note.upid,
            body: {
              ...value,
              creator: note.creator.id,
              ecrid: note.ecrid.ecrid,
              editor: currentUser.id,
              pid: note.pid.product_id,
              response_type: responseType,
              in_response_to: replyTo?.upid,
              released: note.released ?? isCancelled,
            },
          });
          if (!isCancelled) {
            setAlert({
              message: "Update Successful!",
              variant: "success",
            });
          }
        } else {
          await triggerAddNote({
            ...value,
            creator: currentUser.id,
            ecrid: comment.ecrid,
            pid: comment.pid.product_id,
            response_type: responseType,
            in_response_to: replyTo?.upid,
            released: isCancelled,
          });
          if (!isCancelled) {
            setAlert({
              message: "Successfully Created Note!",
              variant: "success",
            });
          }
        }
        if (responseType === ECR_NOTE_RESPONSE_TYPES.VALIDATOR) {
          triggerUpdateEcr({
            ecrid: comment.ecrid,
            response: value.note1,
            responseauthor: currentUser.id,
          });
        }
        handleClose();
        setLoading(false);
      } catch (error) {
        console.error(error);
        if (note?.upid) {
          setError("Error updating note. Please try again.");
        } else {
          setError("Error creating note. Please try again.");
        }
        setLoading(false);
      }
    },
  });

  return (
    <DialogModal
      title={`${ecrTitle()} ECR COMMENT`}
      open={show}
      maxWidth="md"
      fullWidth
      slotProps={{
        backdrop: {
          timeout: 300,
        },
      }}
    >
      <Box>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
            ecrNoteForm.handleSubmit();
          }}
        >
          <Typography id="modal-modal-description" variant="body1">
            <Typography variant="body1" fontWeight="bold" className="mb-3">
              Submitter: {currentUser.username}
            </Typography>
            <Stack spacing={1}>
              {isNiap && (
                <ecrNoteForm.AppField name="private">
                  {(privateField) => (
                    <privateField.YesNoRadioButton
                      label="Is Private?"
                      isHorizontal={true}
                    />
                  )}
                </ecrNoteForm.AppField>
              )}
              {!isLab && !ecrNoteForm.state.values.private && (
                <ecrNoteForm.AppField name="visible_to_labs">
                  {(labsField) => (
                    <labsField.YesNoRadioButton
                      label="Visible To Labs?"
                      isHorizontal={true}
                    />
                  )}
                </ecrNoteForm.AppField>
              )}
              {!isValidator && !ecrNoteForm.state.values.private && (
                <ecrNoteForm.AppField name="visible_to_validators">
                  {(validatorsField) => (
                    <validatorsField.YesNoRadioButton
                      label="Visible To Validators?"
                      isHorizontal={true}
                    />
                  )}
                </ecrNoteForm.AppField>
              )}
            </Stack>
            <Stack spacing={3}>
              <ecrNoteForm.AppField name="gist">
                {(field) => (
                  <field.TextInput
                    label="Title of Note:"
                    disabled={noteType === ECR_NOTE_TYPES.RESPONSE}
                  />
                )}
              </ecrNoteForm.AppField>
              <ecrNoteForm.AppField name="note1">
                {(field) => <field.HtmlEditor label="Overview:" />}
              </ecrNoteForm.AppField>
            </Stack>
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 1,
              p: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Button variant="secondary" onClick={() => handleClose()}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={loading}
              type={loading ? "button" : "submit"}
              data-cy="submit-note"
            >
              {loading && <Spinner animation="border" size="sm" />}
              {note?.upid ? "Update" : "Add"}
            </Button>
          </Box>
          {error && (
            <Alert severity="error" sx={{ mb: 2, width: "100%" }}>
              {error}
            </Alert>
          )}
        </form>
      </Box>
    </DialogModal>
  );
};

export default EcrNoteForm;
